import type { ClassRecord } from '../../trimesters/context/TrimestersContext';
import type { Magazine, Segment } from '../../trimesters/types/trimestersSettings.types';
import { buildLessonId, ClassLesson } from '../types/classLessons';

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const resolveClassSegmentId = (classroom: ClassRecord | null, segments: Segment[]) => {
  if (!classroom) {
    return null;
  }

  if (classroom.segmentId) {
    return classroom.segmentId;
  }

  const classLabel = normalizeText(`${classroom.category} ${classroom.ageRange}`);
  return segments.find((segment) => classLabel.includes(normalizeText(segment.name)))?.id ?? null;
};

export const resolveClassMagazine = (
  classroom: ClassRecord | null,
  currentTrimesterId: string,
  magazines: Magazine[],
  segments: Segment[]
) => {
  const segmentId = resolveClassSegmentId(classroom, segments);

  if (!segmentId) {
    return null;
  }

  return magazines.find((magazine) => magazine.segmentId === segmentId && magazine.trimesterId === currentTrimesterId) ?? null;
};

export const buildClassLessonsFromMagazine = (
  classroom: ClassRecord,
  magazine: Magazine | null,
  classLessonRecords: ClassLesson[]
): ClassLesson[] => {
  if (!magazine) {
    return [];
  }

  return [...magazine.lessons]
    .sort((left, right) => left.number - right.number)
    .map((magazineLesson) => {
      const classLesson =
        classLessonRecords.find((lesson) => lesson.lessonNumber === magazineLesson.number) ??
        classLessonRecords.find((lesson) => lesson.id === buildLessonId(classroom.id, magazineLesson.number));

      return {
        ...(classLesson ?? {}),
        id: classLesson?.id ?? String(magazineLesson.id ?? buildLessonId(classroom.id, magazineLesson.number)),
        classId: classroom.id,
        lessonNumber: magazineLesson.number,
        title: classLesson?.title?.trim() || magazineLesson.title,
        date: classLesson?.date ?? '',
        description: classLesson?.description?.trim() || magazineLesson.summary,
        teacherName: classLesson?.teacherName,
      };
    });
};
